export type Testimonial = {
  name: string
  quote: string
  assets: string
  height: number
  width: number
  zIndex: number
  left: number
  transform: string
}

export type Referral = {
  id?: number
  name?: string
  referreeBonus?: any
  referralExpiration?: any
  referralExpirationInDays?: any
  targetNumDeliveries?: number
}

export type ISO8601String = string

export enum CheckrStatus {
  Waiting = 'WAITING',
  Delayed = 'DELAYED',
  NeedsAttention = 'NEEDS_ATTENTION',
  Clear = 'CLEAR',
}

export enum ShippingStatus {
  NotCreated = 'NOT_CREATED',
  Created = 'CREATED',
  Shipped = 'SHIPPED',
  Delivered = 'DELIVERED',
  Error = 'ERROR',
}

export type ApplicationStatus = {
  step: number
  onboardingProgress: any
  bgcheckStatus: CheckrStatus
  shippingStatus: ShippingStatus
  shippingArrivalDate: ISO8601String
  shippingUrl: string
  shippingAddress: string
  verificationStatus: any
}

export type Applicant = {
  email: string
  firstName: string
  uniqueLink: string
  backgroundCheckCompletionUrl?: string
}

export type GoogleAddress = {
  city: string
  googlePlaceId: string
  lat: number
  lng: number
  printableAddress: string
  shortname: string
  state: string
  street: string
  zipCode: string
}

export type StripeCredentials = {
  publicKey: string
  countryShortname: string
  currencyCode: string
}

export type StripeFileResponse = {
  id: string
  object: string
  created: number
  filename: string
  purpose: string
  size: number
  type: string
  url: string
}

export type Photo = {
  file: File
  blob: Blob
}

export enum Experiment {
  CONTROL = 'control',
  TREATMENT = 'treatment',
}

export type WebviewExperiment = {
  [key: string]: Experiment
}

export enum AlreadyStartedEnum {
  Default = 'default',
  ExistingCredentials = 'existingCredentials',
  LoggedIn = 'loggedIn',
  Closed = 'closed',
}

export type AlreadyStartedSigningUpModal = {
  isShowing?: boolean
  email?: string
  phoneNumber?: string
  type?: AlreadyStartedEnum
}

/**
 * DeliveryIssue is an object representing the details of a given delivery
 * issue that might be of interest to either DoorDash or dashers.
 *
 * **issueType:** String(s) as enums representing what kind of issue it is.
 *
 * **priority:** Number representing how important it is to inform the
 * dasher of this particular issue. Priority 1 is highest.
 */
export type DeliveryIssue = {
  createdAt: string
  deliveryCreatedAt: string
  id: string
  issueType: DeliveryIssueTypes
  priority: number
  status: DeliveryIssueStatus
  steps: DeliveryIssueStep[]
  storeName: string
}

/**
 * The shape of what we parse from the Delivery Issues List response to the
 * redux store so we don't store more than is needed to render the list.
 */
export type DeliveryIssueListItem = {
  deliveryCreatedAt: string
  id: string
  status: DeliveryIssueStatus
  storeName: string
  priority: number
}

/**
 * Different types of possible delivery issues
 */
export enum DeliveryIssueTypes {
  NeverDelivered = 'never_delivered',
}

/**
 * DeliveryIssueStatus: Strings as enums to represent the issue's state. Read
 * and unread are based on whether the dasher views the issue. All other
 * statuses become relevant after the dasher submits a dispute for the issue.
 * It is assumed that all dispute-related statuses imply the issue is read.
 */
export enum DeliveryIssueStatus {
  Unread = 'unread',
  Read = 'read',
  NeedsReview = 'needs_review',
  InReview = 'in_review',
  DisputeAccepted = 'dispute_accepted',
  DisputeRejected = 'dispute_rejected',
}

/**
 * DeliveryIssueStep is an object representing information about an action that
 * dashers should perform as part of the delivery process.
 */
export type DeliveryIssueStep = {
  type: DeliveryIssueStepTypes
  result: DeliveryIssueStepResults
}

/**
 * DeliveryIssueStepTypes: Strings as enums that represent a specific UI
 * element or component describing the step to help dashers understand what
 * is expected of them.
 */
export enum DeliveryIssueStepTypes {
  DxEnteredGeoFence = 'dx_entered_geofence',
  DxContactedCx = 'dx_contacted_cx',
  DxWaited = 'dx_waited',
  DxLeftPhoto = 'dx_left_photo',
}

/**
 * DeliveryIssueStepResults: Strings as enums that represent whether the
 * dasher successfully performed the actions for a given step. Used to in UI
 * for dasher feedback.
 */
export enum DeliveryIssueStepResults {
  Success = 'success',
  Failure = 'failure',
}

export type DeliveryIssueDispute = {
  isSubmitting: boolean
  isSubmitted: boolean
  error?: Error
}

/**
 * Fast Pay response
 */
export type FastPay = {
  country?: string
  currency?: string
  id?: number
  token?: string
  paymentAccountId?: number
  stripeCardId?: string
  last4?: string
  brand?: string
  isDefault?: boolean
  expMonth?: number
  expYear?: number
  createdAt?: Date
  updatedAt?: Date
}

export type VehiclePatchData = {
  make: string
  model: string
  color: string
}

export type GetSales = {
  phone?: string
  timestamp?: string
  isSubmitting: boolean
  wait?: number
}
